import { defineStore } from 'pinia';
import { ref } from 'vue';
// import { type LogInUser, type UserDetails } from './types/User';
import { emitter } from '../core/emitter.js';
import { useRouter } from 'vue-router';
import { useAPI } from '@/composables/useAPI';
import { useAuthStore } from './auth.js';
import { clearOrderKeys } from '@/core/proto/orderKeyManager';
import { PostHogUtils } from '@/core/PostHogUtils';
import { liveSlippageMaxPct } from '@/consts/constants';
import { MarketingSource } from '@/core/marketingSource';
import { canonicalSortFromUser } from '@/core/utils';

export const useUserStore = defineStore(
  'user',
  () => {
    const router = useRouter();
    const authStore = useAuthStore();
    const user = ref({});
    const oddsFormat = ref('american');
    const justSignedUp = ref(false);
    const transactions = ref([]);
    const redirectToDeposit = ref(false);
    const activeTheme = ref('day');
    const passwordHighlighted = ref(false);

    function setPasswordHighlighted(value) {
      passwordHighlighted.value = value;
    }

    function signUp() {}

    function setActiveTheme(value) {
      activeTheme.value = value;
      document.body.setAttribute('theme', value);
      localStorage.setItem('activeTheme', value);
    }

    function setTheme() {
      const storageTheme = localStorage.getItem('activeTheme');
      if (!storageTheme) {
        const defaultTheme = 'day';
        document.body.setAttribute('theme', defaultTheme);
        localStorage.setItem('activeTheme', defaultTheme);
        activeTheme.value = defaultTheme;
      } else {
        document.body.setAttribute('theme', storageTheme);
        activeTheme.value = storageTheme;
      }
    }

    // Snapshot of the settings we want to chart distribution on ("% of users
    // using X"). Only includes keys that are actually present on the user.
    function buildSettingsSnapshot() {
      const u = user.value || {};
      const snap = { display_odds: oddsFormat.value };
      // Pro Mode is three-way (see PRO-189); report the selection rather than a
      // boolean so 'Pregame Only' doesn't get charted as plain 'Pro'.
      if (u.isPro !== undefined)
        snap.pro_mode = u.isPro ? (u.isProLive ? 'Yes' : 'Pregame Only') : 'No';
      if (u.sportsbookDefault !== undefined)
        snap.default_odds_view = u.sportsbookDefault ? 'sportsbook' : 'exchange';
      if (u.viewOddsWithCommission !== undefined)
        snap.view_odds_with_commission = !!u.viewOddsWithCommission;
      if (u.displayConfirmationTicket !== undefined)
        snap.display_confirmation_ticket = !!u.displayConfirmationTicket;
      if (u.defaultSendOrderMessage !== undefined)
        snap.default_send_order_to_chat = !!u.defaultSendOrderMessage;
      if (u.displayRotationNumbers !== undefined)
        snap.display_rotation_numbers = !!u.displayRotationNumbers;
      if (u.defaultArbOffer !== undefined)
        snap.default_arb_offer = !!u.defaultArbOffer;
      if (activeTheme.value) snap.active_theme = activeTheme.value;
      snap.sort_games_by = canonicalSortFromUser(u);
      // Account creation date — lets us define "new user" cohorts (e.g. signed
      // up in the last 30 days) on a stable person property rather than guessing.
      if (u.createdAt) snap.signup_at = u.createdAt;
      // Create-offer unit mode (PRO-173) — snapshot at identify so default-mode
      // users (who never touch the setting) still count in adoption splits.
      snap.create_offer_unit_mode = u.createOfferUnitMode || 'riskAUnit';
      // Quick-add default is a device-local preference (localStorage), so it's
      // not in the server user object; read it here so every identified user
      // carries their current default. Both quick-mode keys are always written
      // together, so the Risk key represents both buttons.
      try {
        snap.quick_bet_amount =
          localStorage.getItem('takebet-risk-quick-mode') || '100';
      } catch {
        // localStorage unavailable — omit rather than throw.
      }
      return snap;
    }

    // Attach the current user to analytics (idempotent). Called from login,
    // signup, and getMe hydration so returning users get identified on refresh.
    function syncAnalyticsIdentity() {
      const u = user.value || {};
      const id = u.id || u._id;
      if (!id || !u.isLoggedIn) return;
      const props = buildSettingsSnapshot();
      if (u.username) props.username = u.username;
      if (justSignedUp.value) {
        props.is_new_account = true;
        // Stamp first-touch acquisition onto the new account's profile so the
        // funnel can be sliced by link. Only on the fresh signup so we never
        // overwrite a returning user's original source on routine getMe syncs.
        // marketing_code is the opaque /r/ code; its meaning lives in the
        // private marketing sheet. utm_source is a fallback for tagged ad links.
        const src = MarketingSource.get();
        if (src) {
          // Stamp every captured dimension we have so none are silently dropped
          // by the clear() below (channel cohorts can be sliced by any of them).
          if (src.marketing_code) props.marketing_code = src.marketing_code;
          if (src.utm_source) props.utm_source = src.utm_source;
          if (src.utm_medium) props.utm_medium = src.utm_medium;
          if (src.utm_campaign) props.utm_campaign = src.utm_campaign;
          if (src.referrer_host) props.referrer_host = src.referrer_host;
          // Consume first-touch attribution after stamping so a later
          // refresh/getMe (justSignedUp persists until logout) can't re-apply
          // signup source to a returning account.
          MarketingSource.clear();
        }
      }
      PostHogUtils.identify(id, props);
    }

    async function setLoggedIn(loggedInUser) {
      try {
        user.value = loggedInUser;
        user.value.defaultSendOrderMessage =
          loggedInUser.defaultSendOrderMessage !== undefined
            ? loggedInUser.defaultSendOrderMessage
            : true;
        user.value.displayConfirmationTicket =
          loggedInUser.displayConfirmationTicket !== undefined
            ? loggedInUser.displayConfirmationTicket
            : false;
        user.value.commissionCharged = loggedInUser.commissionCharged || 0.01;
        user.value.isLoggedIn = true;
        // Live slippage is a DEVICE-LOCAL setting (CTO directive 2026-07-17:
        // the feature is 100% frontend — no server field exists). Hydrate
        // from this user's localStorage snapshot; defaults strict/0.
        syncLiveSlippageFromPersisted();
        user.value.hasMonkeyAccess = evalMonkeyAccess(loggedInUser);
        user.value.userToken = loggedInUser.auth;
        authStore.updateToken(loggedInUser.auth);

        syncAnalyticsIdentity();

        // Load sports data after login - moved to avoid circular dependency
        emitter.emit('loadSportsData');
        emitter.emit('logged-in');
      } catch (error) {
        console.error('an error occured on login ');
      }
    }

    // Live slippage setting — DEVICE-LOCAL, single writer. The user-scoped
    // localStorage key IS the persistence (CTO directive 2026-07-17: 100%
    // frontend, engine and APIs untouched). A save cannot fail and the
    // server never writes these fields, which deletes the entire class of
    // save/hydration races the earlier server-backed design needed guards
    // for. Cross-tab: storage events; cross-device: none (device setting).
    function saveLiveSlippageSetting(mode, pct) {
      const cleanMode = mode === 'slippage' ? 'slippage' : 'strict';
      const cleanPct =
        cleanMode === 'slippage'
          ? Math.max(0, Math.min(liveSlippageMaxPct, Number(pct) || 0))
          : 0;
      user.value.liveSlippageMode = cleanMode;
      user.value.liveSlippagePct = cleanPct;
      writeLiveSlippageSnapshot();
    }

    function setGetMe(gottenUser) {
      if (gottenUser) {
        Object.keys(gottenUser).forEach((key) => {
          // Device-local fields: any environment whose user docs ever held
          // the closed backend design's fields would clobber this device's
          // truth on every getMe (spec-review 25).
          if (key === 'liveSlippageMode' || key === 'liveSlippagePct') {
            return;
          }
          user.value[key] = gottenUser[key];
        });
        user.value.hasMonkeyAccess = evalMonkeyAccess(gottenUser);
        syncAnalyticsIdentity();
      }
    }
    function throttle(func, limit) {
      let inThrottle;
      return function () {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => (inThrottle = false), limit);
        }
      };
    }

    async function callGetMe() {
      let attempt = 0;
      const username = user.value.userName || '';
      const maxAttempts = 3;
      while (attempt < maxAttempts) {
        try {
          const { get } = useAPI('user/getMe/', false, { timeout: 15000 });
          let response = await get();
          response = response === null ? {} : response;
          if (Object.keys(response).length === 0) {
            throw new Error('Invalid response');
          }
          setGetMe(response.user);
          return;
        } catch (error) {
          attempt++;
          if (
            error.message !== 'Invalid response' &&
            error.message !== 'Network Error'
          ) {
            throw new Error(
              `Error requesting getMe for user: ${username} | ${error.message}`,
            );
          }
          if (
            error.message === 'Invalid response' ||
            error.message === 'Network Error'
          ) {
            await new Promise((resolve) => setTimeout(resolve, 750));
          }
        }
      }
      throw new Error(
        `Failed to fetch getMe for user ${username} after 3 attempts`,
      );
    }

    const getMe = throttle(callGetMe, 3000);

    function setSortBy(value) {
      switch (value) {
        case 'default':
          user.value.defaultRotationNumbers = false;
          user.value.matchedVolume = false;
          user.value.openInterest = false;
          break;
        case 'rotation_number':
          user.value.defaultRotationNumbers = true;
          user.value.matchedVolume = false;
          user.value.openInterest = false;
          break;
        case 'matched_volume':
          user.value.defaultRotationNumbers = false;
          user.value.matchedVolume = true;
          user.value.openInterest = false;
          break;
        case 'open_interest':
          user.value.defaultRotationNumbers = false;
          user.value.matchedVolume = false;
          user.value.openInterest = true;
          break;
        default:
          // Handle invalid input
          console.warn(`Invalid sort value: ${value}`);
      }
    }

    async function getTransactions() {
      try {
        const { request } = useAPI('deposit/getTransactions');
        const response = await request();
        transactions.value = response;
      } catch (error) {
        console.log('error getting transactions');
      }
    }

    function setUserEmail(email) {
      user.value.userEmail = email;
    }

    function setRedirectToDeposit(payload) {
      redirectToDeposit.value = payload;
    }

    function selectSportsbookDefault() {}
    function saveEmail() {}

    function selectOddsType(payload) {
      if (payload === '1') {
        oddsFormat.value = 'american';
      } else if (payload === '2') {
        oddsFormat.value = 'decimal';
      } else if (payload === '3') {
        oddsFormat.value = 'impliedProbability';
      }
    }

    function makeJustSignedUpTruthy() {
      justSignedUp.value = true;
    }

    function logOut() {
      user.value;
      user.value.isLoggedIn = false;
    }

    function evalMonkeyAccess(userObj) {
      if (
        [
          '66f1d4e8e9a62505256ce976',
          '634c95e31197a19a08ccdca3',
          '63f6900a415f52659a0e93f1',
          '6244936b5815b1c074217f4e',
          '63f9390893ff64ca6b13c08e',
          '642723d526d536c4896fab3c',
          '66296256cf660097e3327a15',
          '66315138c282951756deaa58',
          '663277f0265ecbcdd78084c3',
          '664fad211169dcd6e489c96b',
        ].includes(userObj.id)
      ) {
        return true;
      } else if (
        ['monkey2', 'baghdaddy', 'evan', 'joedirt', 'mongoose'].includes(
          userObj.username,
        )
      ) {
        return true;
      }
      return false;
    }

    function updateUserToken(token) {
      user.value.userToken = token;
    }

    function reset() {
      user.value = { isLoggedIn: false };
      oddsFormat.value = 'american';
      justSignedUp.value = false;
      transactions.value = [];
      redirectToDeposit.value = false;
      authStore.reset();
      clearOrderKeys();
      PostHogUtils.reset();
    }

    async function verifyUserKYC(sessionID) {
      try {
        const { request } = useAPI('/user/verifyUserKYC');
        const response = await request({ sessionID: sessionID });
      } catch (error) {
        console.log('error getting veriff decision', error);
      } finally {
        await callGetMe();
        // Final KYC outcome from the backend after a Veriff session. Only emits
        // on a real decision (approved/declined) and skips repeats, so routine
        // session polls on login/app-mount don't pollute the funnel.
        PostHogUtils.markKycResult(user.value.veriffData?.['status']);
        if (user.value.veriffData?.['status'] === 'declined') {
          emitter.emit('showAlert', {
            shown: true,
            type: 'veriff-declined',
            status: 'danger',
            title: 'Verification Declined',
            detail: 'Please try again.',
          });
        }
        if (user.value.veriffData?.['status'] === 'approved') {
          emitter.emit('showAlert', {
            shown: true,
            type: 'veriff-approved',
            status: 'ok',
            title: 'Verification Approved',
          });
        }
      }
    }

    // Cross-tab sync for the live-slippage money control, via a DEDICATED
    // localStorage key. Reading the setting back out of the whole-store pinia
    // persistence was ordering-fragile: any stale tab that mutated its store
    // (balance tick, getMe) re-persisted its entire old snapshot and silently
    // reverted the saved tolerance everywhere (spec-review B3). The dedicated
    // key is written only by explicit saves and login hydration, so no
    // unrelated store write can clobber it. The server clamp remains the
    // money guard regardless — this keeps the UI honest.
    // The key is USER-SCOPED (bugbot round 2): a shared key let account B on
    // the same browser inherit account A's tolerance on startup/refocus
    // until getMe corrected it — a money-adjacent window. No user id yet =
    // no snapshot and no sync.
    function liveSlippageStorageKey() {
      const id = user.value?.id;
      return id ? `liveSlippage:${id}` : null;
    }
    function writeLiveSlippageSnapshot() {
      const key = liveSlippageStorageKey();
      if (!key) return;
      try {
        // One-time cleanup of the pre-user-scoped shared key so no stale
        // reader can ever see it again.
        localStorage.removeItem('liveSlippage');
        localStorage.setItem(
          key,
          JSON.stringify({
            mode: user.value.liveSlippageMode || 'strict',
            pct: Number(user.value.liveSlippagePct) || 0,
          }),
        );
      } catch {
        // storage unavailable — the setting degrades to session-only
      }
    }
    // Hydrate the store from THIS device's persisted setting (login,
    // startup, refocus, other-tab writes). This is the read side of the
    // single source of truth; missing/invalid snapshot = strict/0.
    function syncLiveSlippageFromPersisted() {
      const key = liveSlippageStorageKey();
      if (!key) return;
      try {
        const raw = localStorage.getItem(key);
        if (!raw || !user.value?.isLoggedIn) {
          user.value.liveSlippageMode = user.value.liveSlippageMode || 'strict';
          user.value.liveSlippagePct = user.value.liveSlippagePct || 0;
          return;
        }
        const snap = JSON.parse(raw);
        user.value.liveSlippageMode =
          snap?.mode === 'slippage' ? 'slippage' : 'strict';
        user.value.liveSlippagePct = Math.max(
          0,
          Math.min(liveSlippageMaxPct, Number(snap?.pct) || 0),
        );
      } catch {
        // storage unavailable or unparsable — keep the in-memory values
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key && event.key === liveSlippageStorageKey()) {
          syncLiveSlippageFromPersisted();
        }
      });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) syncLiveSlippageFromPersisted();
      });
      // Startup hydration: a reloaded tab restores the whole-store pinia
      // snapshot (possibly stale) and would otherwise keep it until the first
      // refocus. Deferred a tick so the persistence plugin has restored
      // `user` (including isLoggedIn) before the sync's guard runs (bugbot).
      setTimeout(syncLiveSlippageFromPersisted, 0);
    }

    return {
      user,
      transactions,
      redirectToDeposit,
      oddsFormat,
      justSignedUp,
      activeTheme,
      passwordHighlighted,
      setPasswordHighlighted,
      setLoggedIn,
      logOut,
      setSortBy,
      getMe,
      setGetMe,
      getTransactions,
      setRedirectToDeposit,
      selectOddsType,
      makeJustSignedUpTruthy,
      setUserEmail,
      saveLiveSlippageSetting,
      reset,
      updateUserToken,
      setActiveTheme,
      setTheme,
      verifyUserKYC,
    };
  },
  {
    persist: true,
  },
);
